/**
 * `SettingsSkills` — ported from `Foyer_Dashboard_CURRENT.jsx` line 7885.
 *
 * Phase 1 cuts: edit-skill modal is dropped; enable/disable is a Toggle and
 * the remove tooltip is preserved. The 7 defaults come from
 * `@sill/types/default-skills`.
 *
 * ADR-0162 §3 Slice 4 + §3c — "Add custom skill" is now wired to the
 * live `POST /v1/skills` route (Slices 1 / 2a / 2b are all merged +
 * deployed). Custom rows carry a "CUSTOM" pill; origin-dispatched rows
 * (webhook OR mcp_passthrough) carry their fulfillment endpoint +
 * `endpoint_status` (healthy → mint; unreachable → coral) plus "Edit
 * endpoint" / "Rotate secret" affordances that route through
 * `patchWebhookEndpoint` / `rotateWebhookSecret`. The two kinds are
 * UI-symmetric — the framing difference is entirely origin-side.
 * Owner/admin only — the affordances hide for reviewer/viewer even
 * though the server 403s.
 *
 * ADR-0129 §2.5 — each row gains a per-skill status pill + a one-line
 * next-step hint instead of the legacy "DISABLED-only" pill. The route
 * (`GET /v1/skills`) now returns `SkillWithStatus[]` with `status` +
 * optional `gating_reason` derived from the SAME backing signals the
 * signed agent card uses (single source of truth — ADR-0129 §2.1).
 *
 * The merchant's toggle behavior is UNCHANGED — they can still enable
 * or disable any skill. The status REFLECTS the published reality
 * regardless of the toggle (e.g. an enabled `request_refund` on a
 * Stripe-disconnected site reads "NOT LIVE — connect a payment rail
 * (Stripe) to go live"). Per ADR-0129 §2.6, NEVER auto-toggle on the
 * merchant's behalf. Sill exposes truth; the merchant decides.
 *
 * Footer count widened (§2.5): `N live · K enabled · M total` so the
 * gap between "what's on" and "what's actually on the card" is visible
 * at a glance.
 */

import { Link2, Lock, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { Skill, SkillId, SkillWithStatus } from '@sill/types';
import { isStoreSkillId } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { derivePlatformKind } from '../../lib/platform-kind.js';
import { useC } from '../../lib/theme.js';
import {
  skillFulfillmentPathKicker,
  skillHintText,
  skillStatusToPill,
} from '../../lib/skill-status-copy.js';
import {
  Card,
  ConfirmDialog,
  Modal,
  Pill,
  Toggle,
  Tooltip,
} from '../../components/atoms/index.js';
import { BusinessSkillsSection } from './BusinessSkillsSection.js';
import { ConfigureDefaultEndpointModal } from './ConfigureDefaultEndpointModal.js';
import { CustomSkillModal } from './CustomSkillModal.js';
import { EditWebhookEndpointModal } from './EditWebhookEndpointModal.js';
import { SecretShownOnce } from './SecretShownOnce.js';

/**
 * ADR-0165 §Slice B.1 — the default skill_ids whose Track B (merchant
 * HTTPS endpoint) lane is offered from Settings → Skills. The two
 * transactional defaults (`place_order`, `request_refund`) stay first-
 * party through the settlement pipeline and are NOT offered on Track B.
 * Mirrors `responseSchemaFor()` in `@sill/connectors-core`.
 */
const TRACK_B_DEFAULT_SKILL_IDS: ReadonlySet<string> = new Set([
  'browse_catalog',
  'check_availability',
  'order_status',
  'track_shipment',
  'recommend',
]);

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const REMOVE_TOOLTIP = 'Contact support to remove a skill';

export function SettingsSkills(): JSX.Element {
  const C = useC();
  const api = useApi();
  const auth = useOptionalAuth();
  const toast = useToast();
  const { activeSite } = useSites();
  const [skills, setSkills] = useState<SkillWithStatus[]>([]);
  // ADR-0222 — Business skills group is non-store-only. We derive the
  // "store vs website" flag from the shared `derivePlatformKind` helper
  // (visibility report fallback — fine for Settings; the Install view
  // owns the install-coverage read). `null` means "not yet resolved";
  // we fail-soft to `website` after one load attempt so a transient
  // visibility fetch failure does not permanently hide the group.
  const [platform_kind, setPlatformKind] = useState<'store' | 'website' | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // ADR-0165 §Slice B.1 — attach a Track B endpoint to a DEFAULT skill
  // row that currently has no endpoint. Opens the `ConfigureDefaultEndpointModal`
  // (route: widened `POST /v1/skills`).
  const [configureDefaultFor, setConfigureDefaultFor] = useState<Skill | null>(
    null,
  );
  const [editEndpointFor, setEditEndpointFor] = useState<Skill | null>(null);
  const [rotateFor, setRotateFor] = useState<Skill | null>(null);
  // The just-minted secret from a rotate. Non-null → the shown-once
  // panel replaces the confirm dialog; dismissal clears it.
  const [rotatedSecret, setRotatedSecret] = useState<{
    secret: string;
    skill_id: SkillId;
  } | null>(null);
  // ADR-0171 Slice R.1 — the skill_id currently being reprobed. Used
  // to disable the button + swap its label to "Running…" until the
  // synchronous ack lands. NEVER blocks the whole row (a second row's
  // reprobe stays independently clickable).
  const [reprobingId, setReprobingId] = useState<SkillId | null>(null);

  // ADR-0162 §3 Slice 4 — the create/rotate/patch routes are
  // owner/admin-only server-side. The dashboard hides the affordances
  // for reviewer/viewer as UX (the server 403s regardless — this is
  // hide-dead-buttons, not the security boundary).
  const role =
    auth !== null && auth.state.kind === 'authenticated'
      ? auth.state.user.role
      : null;
  const can_write = role === 'owner' || role === 'admin';

  const refetch = async (): Promise<void> => {
    if (!activeSite) return;
    const next = await api.skills.listForSite(activeSite.site_id);
    setSkills(next);
  };

  useEffect(() => {
    if (!activeSite) return;
    void api.skills.listForSite(activeSite.site_id).then(setSkills);
    // ADR-0222 — resolve platform_kind off the visibility report.
    // Fail-soft to `website` on error (safer default — business skills
    // suggestion appears, store commerce skills still show).
    let cancelled = false;
    void api.visibility.getReport(activeSite.site_id).then(
      (r) => {
        if (cancelled) return;
        setPlatformKind(derivePlatformKind({ install: null, visibility: r }));
      },
      () => {
        if (cancelled) return;
        setPlatformKind('website');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, activeSite]);

  if (!activeSite) return <div />;

  const persist = (next: SkillWithStatus[]): void => {
    setSkills(next);
    // The toggle path writes the merchant's `enabled` intent. The
    // route re-derives `status` on the next read; we don't carry the
    // mutation through the save bag (the wire write doesn't include
    // the derived field). ADR-0129 §2.6: never auto-toggle — the
    // merchant remains the write authority.
    void api.skills.saveForSite(activeSite.site_id, next);
  };

  const toggle = (id: SkillId): void => {
    persist(
      skills.map((s) =>
        s.skill_id === id
          ? // Optimistic local update: flip `enabled` and reflect the
            // change in `status`. The next refetch (or a future
            // listForSite revalidation) lands the route's authoritative
            // status. The local mapping is conservative —
            // `enabled → 'live'` is wrong only when backing is also
            // off (the row will refetch as `enabled_unbacked` next),
            // and `enabled → 'disabled'` is the toggle-off case which
            // is already correct.
            ({
              ...s,
              enabled: !s.enabled,
              status: s.enabled ? 'disabled' : 'live',
            } as SkillWithStatus)
          : s,
      ),
    );
  };

  const onCreated = (): void => {
    // Full refetch — the route re-derives status + backing signals for
    // the new row; the created skill's status pill can only come from
    // the origin's authoritative computation.
    void refetch();
  };

  const onEndpointSaved = (): void => {
    void refetch();
  };

  const rotateSecret = async (skill: Skill): Promise<void> => {
    try {
      const res = await api.skills.rotateWebhookSecret(
        activeSite.site_id,
        skill.skill_id,
      );
      setRotatedSecret({ secret: res.webhook_secret, skill_id: skill.skill_id });
    } catch (err) {
      const reason =
        err instanceof ApiError
          ? mapRotateError(err)
          : 'Could not rotate the secret. Try again.';
      toast({ title: 'Rotate failed', body: reason, tone: 'coral' });
    }
  };

  // ADR-0171 Slice R.1 — re-run the first-activation battery. Owner/
  // admin-only server-side; the button is hidden for reviewer/viewer
  // (WebhookRowDetails gates on canWrite). Copy discipline: NEVER
  // reads "endpoint verified" / "secure" — the toast + row copy stay
  // on the "activation checks" language.
  const reprobeActivation = async (skill: Skill): Promise<void> => {
    setReprobingId(skill.skill_id);
    try {
      const res = await api.skills.reprobeActivationBattery(
        activeSite.site_id,
        skill.skill_id,
      );
      // Reflect the fresh status locally so the row updates without a
      // full refetch; the next `listForSite` will confirm.
      setSkills((prev) =>
        prev.map((s) =>
          s.skill_id === skill.skill_id
            ? { ...s, activation_probe_status: res.activation_probe_status }
            : s,
        ),
      );
      toast({
        title:
          res.activation_probe_status === 'passed'
            ? 'Activation checks passed'
            : res.activation_probe_status === 'blocked'
              ? 'Activation checks blocked'
              : 'Activation checks not yet run',
        body:
          res.activation_probe_status === 'passed'
            ? 'This skill can now advertise on your signed agent card.'
            : res.activation_probe_status === 'blocked'
              ? 'The battery caught a configuration issue. See the skill contract docs for the required checks.'
              : 'The battery did not complete. Try again shortly.',
        tone: res.activation_probe_status === 'passed' ? 'mint' : 'coral',
      });
      void refetch();
    } catch (err) {
      const reason =
        err instanceof ApiError
          ? 'Could not re-run the activation battery. Try again.'
          : 'Could not re-run the activation battery. Try again.';
      toast({ title: 'Reprobe failed', body: reason, tone: 'coral' });
    } finally {
      setReprobingId(null);
    }
  };

  // ADR-0222 — the footer count mirrors what the merchant sees. On a
  // non-store site we hide the truly-inert store-only rows above, so
  // the count applies to the same visible set. Enabled / endpoint-
  // attached commerce defaults still show + count.
  const visible_skills =
    platform_kind === 'website'
      ? skills.filter((s) => {
          if (s.is_custom === true) return true;
          if (!isStoreSkillId(String(s.skill_id))) return true;
          if (s.enabled) return true;
          if (typeof s.fulfillment_endpoint === 'string' && s.fulfillment_endpoint.length > 0) return true;
          return false;
        })
      : skills;
  const enabledCount = visible_skills.filter((s) => s.enabled).length;
  const liveCount = visible_skills.filter((s) => s.status === 'live').length;

  return (
    <Card id="skills-list" className="p-5 sm:p-6 mb-5">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          Skills
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          What this site's agent can do. Each skill is a callable A2A capability published on your agent card.
        </div>
        {!can_write && (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              lineHeight: 1.5,
              marginTop: 8,
            }}
          >
            Your role can view skills but cannot change them. Ask an
            owner or admin to toggle, edit, or add skills.
          </div>
        )}
      </div>

      <div className="py-3 space-y-2">
        {skills
          // ADR-0222 — on a non-store site, hide the seven store-only
          // commerce rows ONLY when they are truly inert: no custom
          // row (never hide a custom skill), not currently enabled,
          // and no Track-B endpoint attached. A merchant who has
          // explicitly turned one on or wired an endpoint stays
          // visible — hiding it would be misleading. The common case
          // ("Browse catalog — DISABLED" with no endpoint on a SaaS
          // site) is correctly suppressed.
          .filter((s) => {
            if (platform_kind !== 'website') return true;
            if (s.is_custom === true) return true;
            if (!isStoreSkillId(String(s.skill_id))) return true;
            if (s.enabled) return true;
            if (typeof s.fulfillment_endpoint === 'string' && s.fulfillment_endpoint.length > 0) return true;
            return false;
          })
          .map((s) => {
          const pill = skillStatusToPill(s.status);
          const hint = skillHintText(s.status, s.gating_reason);
          // ADR-0174 §8 — the small "handoff" kicker rendered under
          // place_order's hint when the skill is lit via the WC handoff
          // route (§3 third branch). Absent (null) for every other row
          // + for a settlement-lit place_order — leaves rendering
          // byte-identical for the current shipped cohort. Copy is a
          // marketing-owned placeholder; see the shared helper's
          // docstring.
          const fulfillment_kicker = skillFulfillmentPathKicker(
            s.fulfillment_path,
          );
          const is_custom = s.is_custom === true;
          // ADR-0162 §3c + ADR-0165 §Slice B.1 — both origin-dispatched
          // kinds render the same endpoint sub-row (URL + health pill +
          // edit/rotate). The outbound framing is picked at dispatch
          // time; the merchant UI is kind-agnostic here. ADR-0165
          // widens the sub-row to also apply to DEFAULT skill rows that
          // have been attached to Track B — the wire fields
          // (fulfillment_endpoint, endpoint_status) round-trip
          // identically for both lanes.
          const has_endpoint =
            s.fulfillment_kind === 'webhook'
            || s.fulfillment_kind === 'mcp_passthrough';
          // ADR-0165 — a default skill row that has NOT been wired to
          // Track B is offered a "Configure endpoint" affordance for
          // the read-side default skills. `place_order` /
          // `request_refund` stay first-party (rail-gated, T2).
          const can_configure_default =
            !is_custom
            && !has_endpoint
            && TRACK_B_DEFAULT_SKILL_IDS.has(String(s.skill_id));
          return (
            <div
              key={s.skill_id}
              data-testid={`skill-row-${s.skill_id}`}
              className="flex items-center gap-3 p-3"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                // Dim disabled rows. Other non-live statuses stay at full
                // opacity so the merchant can still read the hint copy
                // without squinting — they're enabled, just not live yet.
                opacity: s.status === 'disabled' ? 0.55 : 1,
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                    {s.name}
                  </span>
                  {s.auth_required && (
                    <Tooltip content="Requires a signed mandate. Agent must present a signed Intent before calling.">
                      <span>
                        <Pill tone="amber" icon={Lock}>
                          SIGNED
                        </Pill>
                      </span>
                    </Tooltip>
                  )}
                  {is_custom && (
                    <span data-testid={`skill-custom-pill-${s.skill_id}`}>
                      <Pill tone="neutral">CUSTOM</Pill>
                    </span>
                  )}
                  <span data-testid={`skill-pill-${s.skill_id}`}>
                    <Pill tone={pill.tone}>{pill.label}</Pill>
                  </span>
                </div>
                <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
                  {s.skill_id}
                </div>
                {hint !== null && (
                  <div
                    data-testid={`skill-hint-${s.skill_id}`}
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {hint}
                  </div>
                )}
                {fulfillment_kicker !== null && (
                  <div
                    data-testid={`skill-fulfillment-kicker-${s.skill_id}`}
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      color: C.muted,
                      marginTop: 4,
                      letterSpacing: 0.3,
                      textTransform: 'uppercase',
                    }}
                  >
                    {fulfillment_kicker}
                  </div>
                )}
                {has_endpoint && (
                  <WebhookRowDetails
                    skill={s}
                    canWrite={can_write}
                    onEdit={() => setEditEndpointFor(s)}
                    onRotate={() => setRotateFor(s)}
                    onReprobe={() => void reprobeActivation(s)}
                    reprobeBusy={reprobingId === s.skill_id}
                  />
                )}
                {can_configure_default && can_write && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setConfigureDefaultFor(s)}
                      data-testid={`configure-endpoint-${s.skill_id}`}
                      className="flex items-center gap-1.5 px-2.5 py-1"
                      style={{
                        background: 'transparent',
                        border: `1px solid ${C.border}`,
                        color: C.text,
                        fontFamily: sansFont,
                        fontSize: 11,
                        borderRadius: 2,
                      }}
                    >
                      <Link2 size={11} /> Configure endpoint
                    </button>
                    <span
                      style={{
                        fontFamily: sansFont,
                        fontSize: 11,
                        color: C.muted,
                      }}
                    >
                      Serve this skill from your own HTTPS endpoint.
                    </span>
                  </div>
                )}
              </div>
              <Tooltip content={REMOVE_TOOLTIP} side="left">
                <button
                  type="button"
                  aria-disabled="true"
                  aria-label={`Remove ${s.name}`}
                  className="p-1.5 flex-shrink-0"
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: C.muted,
                    opacity: 0.45,
                    cursor: 'not-allowed',
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </Tooltip>
              <Toggle
                on={s.enabled}
                onChange={() => toggle(s.skill_id)}
                aria-label={`Toggle ${s.name}`}
                disabled={!can_write}
              />
            </div>
          );
        })}
      </div>
      {/* ADR-0224 — show Business skills on every site type. Stores see */}
      {/* the group too with an "optional for stores" header line; all */}
      {/* seven slots default to off. */}
      <BusinessSkillsSection
        site_id={activeSite.site_id}
        site_domain={activeSite.domain ?? ''}
        platform_kind={platform_kind}
      />
      {can_write && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="add-custom-skill"
            className="flex items-center justify-center gap-2 px-4 py-2"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontFamily: sansFont,
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            <Plus size={13} /> Add custom skill
          </button>
        </div>
      )}
      <div className="pt-4 flex items-center">
        <span
          data-testid="skills-footer-count"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
        >
          {liveCount} live · {enabledCount} enabled · {visible_skills.length} total
        </span>
      </div>

      <CustomSkillModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        site_id={activeSite.site_id}
        onCreated={onCreated}
      />
      {configureDefaultFor !== null && (
        <ConfigureDefaultEndpointModal
          open={configureDefaultFor !== null}
          onClose={() => setConfigureDefaultFor(null)}
          site_id={activeSite.site_id}
          skill={configureDefaultFor}
          onConfigured={() => {
            setConfigureDefaultFor(null);
            void refetch();
          }}
        />
      )}
      {editEndpointFor !== null && (
        <EditWebhookEndpointModal
          open={editEndpointFor !== null}
          onClose={() => setEditEndpointFor(null)}
          site_id={activeSite.site_id}
          skill={editEndpointFor}
          onSaved={() => onEndpointSaved()}
        />
      )}
      <ConfirmDialog
        open={rotateFor !== null && rotatedSecret === null}
        onClose={() => setRotateFor(null)}
        icon={RefreshCw}
        title="Rotate webhook secret?"
        confirmLabel="Rotate secret"
        confirmTone="amber"
        body={
          rotateFor !== null ? (
            <span>
              Rotating invalidates the current secret for{' '}
              <strong>{rotateFor.name}</strong>. Your endpoint will fail
              signature verification until you deploy the new secret.
            </span>
          ) : (
            ''
          )
        }
        consequences={[
          'A new shared secret is minted.',
          'The new secret is shown ONCE — copy it before dismissing.',
          'Outbound `X-Sill-Signature` headers use the new secret immediately.',
        ]}
        onConfirm={() => {
          if (rotateFor !== null) void rotateSecret(rotateFor);
        }}
      />
      {rotatedSecret !== null && (
        <Modal
          open={rotatedSecret !== null}
          onClose={() => {
            setRotatedSecret(null);
            setRotateFor(null);
          }}
          size="md"
          title="Copy your new webhook secret"
          subtitle="Shown once. Sill never displays it again."
        >
          <div className="p-6 space-y-4">
            <SecretShownOnce secret={rotatedSecret.secret} />
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setRotatedSecret(null);
                  setRotateFor(null);
                }}
                className="flex-1 sm:flex-none px-6 py-2.5"
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 2,
                }}
              >
                I have copied the secret
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function mapRotateError(err: ApiError): string {
  if (err.kind === 'rate_limited') {
    return 'Too many changes — try again in a moment.';
  }
  if (err.server_reason === 'role_insufficient') {
    return 'Only owners and admins can rotate secrets.';
  }
  if (err.kind === 'forbidden' || err.kind === 'not_found') {
    return 'You do not have permission to rotate this secret.';
  }
  if (err.kind === 'service_unavailable') {
    return 'The service is temporarily unavailable. Try again in a moment.';
  }
  return 'Could not rotate the secret. Try again.';
}

interface WebhookRowDetailsProps {
  skill: SkillWithStatus;
  canWrite: boolean;
  onEdit: () => void;
  onRotate: () => void;
  onReprobe: () => void;
  reprobeBusy: boolean;
}

/**
 * The extra row content for a custom webhook skill: the truncated
 * endpoint URL + a small status pill (mint / coral) + Edit / Rotate
 * affordances (hidden for reviewer/viewer). Kept as a small
 * sub-component so the main row body stays readable.
 *
 * ADR-0171 Slice R.1 — a Track-B DEFAULT row also renders the
 * `activation_probe_status` outcome + a "Re-run activation battery"
 * affordance. Copy discipline: the label reads "activation checks
 * passed" / "blocked" / "not yet run" — NEVER "endpoint verified" or
 * "secure". The battery is a hygiene gate, not a security certification.
 */
function WebhookRowDetails({
  skill,
  canWrite,
  onEdit,
  onRotate,
  onReprobe,
  reprobeBusy,
}: WebhookRowDetailsProps): JSX.Element {
  const C = useC();
  const healthy = skill.endpoint_status === 'healthy';
  // The activation-probe wire field is present only on the Track-B
  // DEFAULT lane (a CUSTOM row's status stays undefined). Render the
  // pill + status label only when the field is present + one of the
  // three closed values.
  const probe_status = skill.activation_probe_status;
  const has_probe =
    probe_status === 'passed'
    || probe_status === 'blocked'
    || probe_status === 'not_run';
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          data-testid={`skill-endpoint-${skill.skill_id}`}
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 320,
          }}
          title={skill.fulfillment_endpoint ?? ''} /* title-ok: full value of the ellipsised endpoint */
        >
          {skill.fulfillment_endpoint ?? '—'}
        </span>
        {skill.endpoint_status !== undefined && (
          <Pill tone={healthy ? 'mint' : 'coral'}>
            {healthy ? 'HEALTHY' : 'UNREACHABLE'}
          </Pill>
        )}
        {has_probe && (
          <span
            data-testid={`skill-activation-status-${skill.skill_id}`}
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
            }}
          >
            {probe_status === 'passed'
              ? 'activation checks passed'
              : probe_status === 'blocked'
                ? 'activation checks blocked'
                : 'activation checks not yet run'}
          </span>
        )}
      </div>
      {canWrite && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onEdit}
            data-testid={`edit-endpoint-${skill.skill_id}`}
            className="px-2.5 py-1"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              fontFamily: sansFont,
              fontSize: 11,
              borderRadius: 2,
            }}
          >
            Edit endpoint
          </button>
          <button
            type="button"
            onClick={onRotate}
            data-testid={`rotate-secret-${skill.skill_id}`}
            className="px-2.5 py-1"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              fontFamily: sansFont,
              fontSize: 11,
              borderRadius: 2,
            }}
          >
            Rotate secret
          </button>
          {has_probe && (
            <button
              type="button"
              onClick={onReprobe}
              disabled={reprobeBusy}
              data-testid={`reprobe-activation-${skill.skill_id}`}
              className="px-2.5 py-1"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 11,
                borderRadius: 2,
                opacity: reprobeBusy ? 0.5 : 1,
                cursor: reprobeBusy ? 'not-allowed' : 'pointer',
              }}
            >
              {reprobeBusy ? 'Running…' : 'Re-run activation battery'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
